import getConfig from "config";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { gte, lt } from "semver";

import {
  showUpdateAvailableToast,
  UPDATE_TOAST_ID
} from "features/common/toasts/pwa";
import { dismissToast as dismissToastComp } from "features/common/toasts/toast";
import { useLocalStorageBlocked } from "features/localStorageBlocked";
import { isNativeAppiOS } from "features/pwa/mobile/ios/utils/isNativeAppiOS";
import { getIosAppVersion } from "features/subscriptions/features/webview-ios/hooks/getIosAppVersion";

import { setInitialPwaConfig } from "../pwa/actions";
import { analyticsLogForcesUpdateWithoutUserInput } from "../pwa/analytics";
import {
  dismissPrompts,
  getLastSeenPrompt,
  getPWAState,
  setLastSeenPrompt,
  shouldShowWarning,
  showWarning
} from "../state";
import useUpdateApp from "./useUpdateApp";

export default function useShowPWAUpdateToastEffect() {
  useInitialPwaConfig();
  useShouldShowWarning();
  useForceUpdate();
  useShowHideToasts();
  useLocalStorageSync();
}

function useInitialPwaConfig() {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setInitialPwaConfig());
  }, [dispatch]);
}

function useShouldShowWarning() {
  const dispatch = useDispatch();

  const show = useSelector(shouldShowWarning);

  useEffect(() => {
    if (show) {
      dispatch(showWarning());
    }
  }, [dispatch, show]);
}

function useForceUpdate() {
  // This is a last ditch for us to be able to force updates for really broken versions
  const { update } = useUpdateApp();
  const { newVersionAvailable, shouldForceUpdate } = useSelector(getPWAState);

  useEffect(() => {
    if (shouldForceUpdate && newVersionAvailable) {
      analyticsLogForcesUpdateWithoutUserInput();
      update();
    }
  }, [shouldForceUpdate, newVersionAvailable, update]);
}

function useShowHideToasts() {
  const dispatch = useDispatch();
  const { update } = useUpdateApp();
  const { showingWarning, promptHidden, promptDismissed } =
    useSelector(getPWAState);

  const dismissPrompt = useCallback(() => {
    dispatch(dismissPrompts(Date.now()));
  }, [dispatch]);

  const shouldUpdateIosApp = getShouldUpdateIosApp();
  const onUpdateClick = shouldUpdateIosApp ? updateIosApp : update;

  useEffect(() => {
    if (showingWarning || shouldUpdateIosApp) {
      showUpdateAvailableToast({
        onUpdateClick,
        showForcefulUpdatePrompt: showingWarning,
        onCloseClick: dismissPrompt
      });
    }
  }, [showingWarning, shouldUpdateIosApp, dismissPrompt, onUpdateClick]);

  useEffect(() => {
    if (promptDismissed || promptHidden) {
      dismissToastComp(UPDATE_TOAST_ID);
    }
  }, [dispatch, promptDismissed, promptHidden]);
}

function useLocalStorageSync() {
  const dispatch = useDispatch();
  const { blocked, ready } = useLocalStorageBlocked();
  const toastLastSeen = useSelector(getLastSeenPrompt);

  useEffect(() => {
    if (!blocked && ready) {
      const lastSeen = getToastLastSeenFromLocalStorage();

      dispatch(setLastSeenPrompt(lastSeen));
    }
  }, [dispatch, blocked, ready]);

  useEffect(() => {
    if (toastLastSeen && !blocked && ready) {
      setToastLastSeenInStorage(toastLastSeen);
    }
  }, [toastLastSeen, blocked, ready]);
}

const LOCAL_STORAGE_KEY = "pwa_toast_last_seen";

function getToastLastSeenFromLocalStorage() {
  const value = window.localStorage.getItem(LOCAL_STORAGE_KEY);
  return value === null ? null : Number(value);
}

function setToastLastSeenInStorage(now: string) {
  window.localStorage.setItem(LOCAL_STORAGE_KEY, now);
}

function getShouldUpdateIosApp() {
  try {
    const isIosApp = isNativeAppiOS();
    const appVersion = getIosAppVersion();
    if (!isIosApp || !appVersion) return false;

    const isAppStoreUpdateSupported = gte(appVersion, "1.2.1");
    if (!isAppStoreUpdateSupported) return false;

    const { redirectToAppStoreUpdateForVersionLessThan } = getConfig().ios;

    return lt(appVersion, redirectToAppStoreUpdateForVersionLessThan);
  } catch {
    return false;
  }
}

function updateIosApp() {
  const { appStoreUrl } = getConfig().ios;
  window.location.assign(appStoreUrl);
}
